import { useCallback, useEffect, useState } from "react";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";

export interface ComplaintValue {
  code: string;
  value: string;
}

export type ComplaintValuesStatus = "loading" | "error" | "ready";

/**
 * Loads one complaint's saved answers for a complaint-history screen.
 *
 * - Keeps the store's current complaint id aligned with the URL, so saves land on the right complaint
 *   after a refresh. Without it `updateComplaintValue` silently no-ops (no current header).
 * - `status` is "ready" only once the saved answers have loaded (even if there were none). Screens must
 *   not re-save their whole form unless ready: the form may not reflect what's saved, and re-saving
 *   would overwrite it.
 * - A response for a complaint the screen has moved away from is ignored.
 */
export function useComplaintValues(complaintId: string | null) {
  const fetchComplaintValues = usePrimaryComplaintStore((s) => s.fetchComplaintValues);
  const [values, setValues] = useState<ComplaintValue[] | null>(null);
  const [status, setStatus] = useState<ComplaintValuesStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (complaintId == null) return;
    const state = usePrimaryComplaintStore.getState();
    if (String(state.currentComplaintHeader?.id ?? "") !== complaintId) {
      // A header for another complaint must not lend this one its type/region/name: edit mode would
      // prefill (and then save) the wrong reason and body area. Only the id is known here.
      usePrimaryComplaintStore.setState({
        currentComplaintHeader: { complaintType: "", region: "", description: undefined, id: Number(complaintId) },
      });
    }
  }, [complaintId]);

  useEffect(() => {
    if (complaintId == null) {
      // Nothing to load; never leave the screen on a permanent skeleton.
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setValues(null);
    // patientID is not needed: the store loads values by complaint id alone.
    fetchComplaintValues(undefined, complaintId)
      .then((data) => {
        if (cancelled) return;
        setValues(((data as ComplaintValue[] | undefined) ?? []).filter(Boolean));
        setStatus("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [complaintId, fetchComplaintValues, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { values, status, retry };
}
